import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";
import { procedureExecutionQueryRootKey } from "./useProcedureExecution";
import { WorkOrderUpdateError } from "./useWorkOrderUpdateMutation";

type CompleteWorkOrderInput = {
  workOrderId: WorkOrderId;
  completionNotes: string;
};

type CancelWorkOrderInput = {
  workOrderId: WorkOrderId;
  cancellationReason: string;
};

export function useCancelWorkOrderMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workOrderId,
      cancellationReason,
    }: CancelWorkOrderInput) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].cancel.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId: String(workOrderId),
        },
        json: { cancellationReason },
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(
          body?.error ?? "Unable to cancel Work Order. Try again.",
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order canceled.", { icon: true });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: procedureExecutionQueryRootKey,
        }),
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
      ]);
    },
    onError: (error) =>
      toast.error(error.message || "Unable to cancel Work Order. Try again.", {
        icon: true,
      }),
  });
}

type DeleteWorkOrderInput = {
  workOrderId: WorkOrderId;
};

export function useDeleteWorkOrderMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workOrderId }: DeleteWorkOrderInput) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].$delete({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId: String(workOrderId),
        },
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? "Unable to delete Work Order.");
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order deleted.", { icon: true });
      // Deliberately not invalidating "work-order-detail": the deleted row
      // is excluded from ordinary reads, so refetching it here would 404 the
      // still-mounted detail screen (which is about to navigate away) before
      // its own confirmation/navigation logic runs.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
      ]);
    },
    onError: () =>
      toast.error("Unable to delete Work Order. Try again.", { icon: true }),
  });
}

export function useRestoreWorkOrderMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      workOrderId,
      version,
    }: {
      workOrderId: WorkOrderId;
      version: string;
    }) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].$patch({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId: String(workOrderId),
        },
        json: { statusCode: "OPEN", version },
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: string;
          code?: string;
        } | null;
        throw new WorkOrderUpdateError(
          body?.error ?? `Unable to restore Work Order (${response.status}).`,
          body?.code,
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order restored.", { icon: true });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: procedureExecutionQueryRootKey,
        }),
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
        queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
      ]);
    },
    onError: async (error, variables) => {
      if (
        error instanceof WorkOrderUpdateError &&
        error.code === "WORK_ORDER_STALE"
      ) {
        await queryClient.invalidateQueries({
          queryKey: [
            "work-order-detail",
            selectedCustomer?.id,
            selectedFacility?.id,
            variables.workOrderId,
          ],
        });
        toast.error("Work Order changed. Refreshing the latest version.", {
          icon: true,
        });
        return;
      }
      toast.error("Unable to restore Work Order. Try again.", { icon: true });
    },
  });
}

export function useCompleteWorkOrderMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CompleteWorkOrderInput) => {
      if (!selectedCustomer || !selectedFacility) {
        throw new Error("A customer and facility must be selected.");
      }

      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].complete.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId: String(input.workOrderId),
        },
        json: {
          completionNotes: input.completionNotes,
        },
      });

      if (!response.ok) {
        const errorBody = (await response.json().catch(() => null)) as {
          error?: string;
          failures?: { label?: string }[];
        } | null;
        const unmetItems = (errorBody?.failures ?? [])
          .map((failure) => failure.label)
          .filter((label): label is string => Boolean(label));
        const message = errorBody?.error ?? "Unable to complete Work Order.";
        throw new Error(
          unmetItems.length
            ? `${message} Unanswered: ${unmetItems.join(", ")}.`
            : message,
        );
      }

      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order completed.", { icon: true });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: procedureExecutionQueryRootKey,
        }),
        queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
        queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
      ]);
    },
    onError: () => {
      toast.error("Unable to complete Work Order. Try again.", { icon: true });
    },
  });
}
